import { AxiosResponse } from 'axios';
import { ResponseWithPagination } from '@/types';
import { InterviewTechnicalChallenge } from '@/types/api';
import { InterviewChallengeRequestStatusFilter } from '@/types/api/interviewTechnicalChallenge';
import { supabase } from '@/lib/supabase';
import { asResponse, paginationRange, buildPaginatedResponse } from '@/lib/supabase-helpers';

const listInterviewTechnicalChallenges = async ({
  page,
  take,
  status,
}: {
  page: number;
  take: number;
  status?: InterviewChallengeRequestStatusFilter;
}): Promise<AxiosResponse<ResponseWithPagination<InterviewTechnicalChallenge[]>>> => {
  const [from, to] = paginationRange(page, take);

  let query = supabase
    .from('interview_technical_challenges')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(from, to);

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error, count } = await query;

  if (error) throw error;

  const totalCount = count || 0;

  return asResponse<ResponseWithPagination<InterviewTechnicalChallenge[]>>(buildPaginatedResponse((data || []) as unknown as InterviewTechnicalChallenge[], totalCount, page, take) as any);
};

const editInterviewTechnicalChallenge = async ({
  interviewTechnicalChallengeId,
  interviewTechnicalChallenge,
}: {
  interviewTechnicalChallengeId: string;
  interviewTechnicalChallenge: Partial<InterviewTechnicalChallenge>;
}): Promise<AxiosResponse<InterviewTechnicalChallenge>> => {
  const updatePayload: Record<string, any> = {};

  if (interviewTechnicalChallenge.status !== undefined)
    updatePayload.status = interviewTechnicalChallenge.status;
  if (interviewTechnicalChallenge.githubProjectUrl !== undefined)
    updatePayload.github_project_url = interviewTechnicalChallenge.githubProjectUrl;
  if (interviewTechnicalChallenge.gitlabProjectUrl !== undefined)
    updatePayload.gitlab_project_url = interviewTechnicalChallenge.gitlabProjectUrl;
  if (interviewTechnicalChallenge.technicalVideoUrl !== undefined)
    updatePayload.technical_video_url = interviewTechnicalChallenge.technicalVideoUrl;
  if (interviewTechnicalChallenge.technicalDocumentTitle !== undefined)
    updatePayload.technical_document_title = interviewTechnicalChallenge.technicalDocumentTitle;
  if (interviewTechnicalChallenge.technicalDocumentContent !== undefined)
    updatePayload.technical_document_content = interviewTechnicalChallenge.technicalDocumentContent;
  if (interviewTechnicalChallenge.skillId !== undefined)
    updatePayload.skill_id = interviewTechnicalChallenge.skillId;

  const { data, error } = await supabase
    .from('interview_technical_challenges')
    .update(updatePayload)
    .eq('id', interviewTechnicalChallengeId)
    .select()
    .single();

  if (error) throw error;
  return asResponse(data as unknown as InterviewTechnicalChallenge);
};

const denyInterviewTechnicalChallengeReview = async ({
  interviewTechnicalChallengeId,
}: {
  interviewTechnicalChallengeId: string;
}): Promise<AxiosResponse<InterviewTechnicalChallenge>> => {
  const { data, error } = await supabase
    .from('interview_technical_challenges')
    .update({ status: 'DENIED' })
    .eq('id', interviewTechnicalChallengeId)
    .select()
    .single();

  if (error) throw error;
  return asResponse(data as unknown as InterviewTechnicalChallenge);
};

export default {
  listInterviewTechnicalChallenges,
  editInterviewTechnicalChallenge,
  denyInterviewTechnicalChallengeReview,
};
